import { useRef, useState, type CSSProperties, type ReactNode } from 'react'
import styles from './Button.module.css'
import { ButtonSpinner } from './Spinner'

/* FFButtonWidget port.
 *
 * Faithful behaviours:
 *  - showLoadingIndicator (default true): while onPressed's promise is
 *    pending the label is replaced by a 23x23 spinner in the text colour and
 *    re-entry is blocked (`if (loading) return`).
 *  - The width is held during loading so the button doesn't collapse.
 *  - `finally { if (mounted) setLoading(false) }` — we mirror the mounted
 *    check with a ref so an unmount mid-await doesn't warn.
 *
 * Not ported (unused by every button in the app): hoverColor/hoverTextColor/
 * hoverBorderSide/hoverElevation/disabledColor/disabledTextColor/splashColor/
 * icon support/maxLines>1/textAlign/fontSize==0 icon-only trick. */

export type ButtonVariant = 'filledPrimary' | 'outlinedLight'

interface ButtonProps {
  text: string
  onClick?: () => void | Promise<void>
  variant?: ButtonVariant
  /** Geometry overrides, FFButtonOptions-style. */
  width?: number | string
  height?: number | string
  radius?: number
  elevation?: 0 | 2 | 3
  showLoadingIndicator?: boolean
  disabled?: boolean
  /** Overrides for one-off styling (e.g. Go back's rgba white bg). */
  style?: CSSProperties
  children?: ReactNode
}

export function Button({
  text,
  onClick,
  variant = 'filledPrimary',
  width,
  height,
  radius,
  elevation,
  showLoadingIndicator = true,
  disabled = false,
  style,
}: ButtonProps) {
  const [loading, setLoading] = useState(false)
  // DOM ref doubles as the `mounted` check: it is null after unmount, which
  // mirrors Flutter's `if (mounted) setState(...)` in the finally block.
  const ref = useRef<HTMLButtonElement>(null)

  const handleClick = async () => {
    if (!onClick) return
    if (!showLoadingIndicator) {
      void onClick()
      return
    }
    if (loading) return
    setLoading(true)
    try {
      await onClick()
    } finally {
      if (ref.current) setLoading(false)
    }
  }

  const vars: CSSProperties = {
    ...(width !== undefined && { ['--_w' as string]: cssSize(width) }),
    ...(height !== undefined && { ['--_h' as string]: cssSize(height) }),
    ...(radius !== undefined && { ['--_radius' as string]: `${radius}px` }),
    ...(elevation !== undefined && {
      ['--_shadow' as string]: `var(--elevation-${elevation})`,
    }),
    ...style,
  }

  // Spinner colour = options.textStyle?.color ?? white.
  const spinnerColor = variant === 'outlinedLight' ? '#000000' : '#ffffff'

  return (
    <button
      ref={ref}
      type="button"
      className={`${styles.button} ${styles[variant]}`}
      style={vars}
      onClick={handleClick}
      disabled={disabled}
    >
      {loading ? <ButtonSpinner color={spinnerColor} /> : text}
    </button>
  )
}

function cssSize(v: number | string): string {
  return typeof v === 'number' ? `${v}px` : v
}
